import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { Button, Form, InputGroup } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import {
    FiMail, FiPhone, FiUsers, FiCheckCircle, FiClock, FiAlertCircle, FiMessageSquare
} from "react-icons/fi";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment-timezone";

const NotificationCustomers = () => {
    const [customers, setCustomers] = useState([]);
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [activeFilter, setActiveFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const fetchingRef = useRef(false);

    const getCustomerStatus = (customer) => {
        const reports = customer.reports || [];
        const currentMonth = moment().tz("Asia/Singapore").format("MMM YYYY");
        const currentMonthReport = reports.find(r => r.month === currentMonth);
        return currentMonthReport ? currentMonthReport.status : "No Data";
    };

    const getStatusConfig = (status) => {
        switch (status) {
            case "Paid": return { bg: "#dcfce7", iconColor: "#16a34a", emailColor: "#10b981", whatsappColor: "#25D366" };
            case "Pending": return { bg: "#fef9c3", iconColor: "#f59e0b", emailColor: "#f59e0b", whatsappColor: "#25D366" };
            case "Unpaid": return { bg: "#fee2e2", iconColor: "#ef4444", emailColor: "#ef4444", whatsappColor: "#25D366" };
            default: return { bg: "#f3f4f6", iconColor: "#9ca3af", emailColor: "#6b7280", whatsappColor: "#25D366" };
        }
    };

    const getFirstLetter = (email_address) => {
        if (!email_address) return '?';
        return email_address.charAt(0).toUpperCase();
    };

    const getFilteredCustomers = useMemo(() => {
        let filtered = customers;
        if (activeFilter !== 'all') {
            filtered = filtered.filter(customer => {
                const status = getCustomerStatus(customer);
                return status.toLowerCase() === activeFilter.toLowerCase();
            });
        }
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(customer =>
                customer.full_name?.toLowerCase().includes(term) ||
                customer.email_address?.toLowerCase().includes(term) ||
                customer.phone_number?.toLowerCase().includes(term)
            );
        }
        return filtered;
    }, [customers, activeFilter, searchTerm]);

    const totalSelected = selectedCustomers.length;
    const isAllFilteredSelected = useMemo(() => {
        const filteredIds = getFilteredCustomers.map(c => c.customer_id);
        return filteredIds.length > 0 &&
            filteredIds.every(id => selectedCustomers.includes(id));
    }, [getFilteredCustomers, selectedCustomers]);

    const fetchCustomers = useCallback(async () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        try {
            setLoading(true);
            const res = await axios.get("https://www.azzenterprises.com/api/customers");
            setCustomers(res.data || []);
        } catch (err) {
            toast.error("Failed to fetch customers");
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, []);

    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    const handleCustomerSelect = useCallback((customerId) => {
        setSelectedCustomers(prev => {
            if (prev.includes(customerId)) {
                return prev.filter(id => id !== customerId);
            } else {
                return [...prev, customerId];
            }
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        const filteredIds = getFilteredCustomers.map(c => c.customer_id);
        if (isAllFilteredSelected) {
            setSelectedCustomers(prev => prev.filter(id => !filteredIds.includes(id)));
        } else {
            setSelectedCustomers(prev => {
                const newSet = new Set([...prev, ...filteredIds]);
                return Array.from(newSet);
            });
        }
    }, [getFilteredCustomers, isAllFilteredSelected]);

    const handleFilterChange = useCallback((filter) => {
        setActiveFilter(filter);
        setSelectedCustomers([]);
    }, []);

    const sendEmailNotifications = useCallback(() => {
        if (totalSelected === 0) {
            toast.warning("Please select customers");
            return;
        }
        toast.success(`${totalSelected} email(s) sent!`);
    }, [totalSelected]);

    const sendWhatsappNotifications = useCallback(() => {
        if (totalSelected === 0) {
            toast.warning("Please select customers");
            return;
        }
        toast.success(`${totalSelected} WhatsApp(s) sent!`);
    }, [totalSelected]);

    const renderCustomerItem = (customer) => {
        const customerStatus = getCustomerStatus(customer);
        const statusConfig = getStatusConfig(customerStatus);
        const isSelected = selectedCustomers.includes(customer.customer_id);
        const firstLetter = getFirstLetter(customer.email_address);

        return (
            <div
                className={`customer-item px-3 py-2 mb-2 d-flex align-items-center gap-3 transition-all rounded-3 bg-light ${isSelected ? 'bg-primary-subtle shadow-sm' : 'hover-primary-bg'}`}
                style={{ fontSize: '0.875rem', cursor: 'pointer' }}
                onClick={(e) => {
                    if (!e.target.closest('input[type="checkbox"]')) {
                        handleCustomerSelect(customer.customer_id);
                    }
                }}
            >
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleCustomerSelect(customer.customer_id)}
                    className="form-check-input flex-shrink-0 me-1"
                    style={{ transform: "scale(0.9)", accentColor: "#0d6efd", cursor: 'pointer' }}
                />

                <div
                    className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                    style={{
                        width: '36px',
                        height: '36px',
                        backgroundColor: statusConfig.bg,
                        color: statusConfig.iconColor,
                        fontSize: '0.75rem',
                        fontWeight: '700'
                    }}
                >
                    {firstLetter}
                </div>

                <div className="flex-grow-1 min-w-0">
                    <div className="fw-semibold text-truncate mb-1" style={{ fontSize: '0.9rem' }}>
                        {customer.full_name || '—'}
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: '0.8rem' }}>
                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                            <FiMail size={12} style={{ color: statusConfig.emailColor }} />
                            <span className="text-muted">
                                {customer.email_address || '—'}
                            </span>
                        </div>
                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                            <FiPhone size={12} style={{ color: statusConfig.whatsappColor }} />
                            <span className="text-muted text-truncate" style={{ maxWidth: '120px' }}>
                                {customer.phone_number || '—'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const getFilterButtonStyle = (filter) => {
        const colors = {
            all: { bg: '#eff6ff', color: '#1e40af', border: '#3b82f6' },
            unpaid: { bg: '#fef2f2', color: '#b91c1c', border: '#ef4444' },
            pending: { bg: '#fffbeb', color: '#b45309', border: '#f59e0b' },
            paid: { bg: 'f0fdf4', color: '#15803d', border: '#10b981' }
        };
        const color = colors[filter];
        return {
            fontSize: '0.8rem',
            backgroundColor: activeFilter === filter ? color.bg : '#f8fafc',
            color: activeFilter === filter ? color.color : '#475569',
            border: activeFilter === filter ? `2px solid ${color.border}` : '1px solid transparent',
            padding: '0.5rem 0.75rem'
        };
    };

    return (
        <div className="container-fluid py-3 min-vh-100">
            <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded-3">
                <div className="d-flex align-items-center gap-3">
                    <div>
                        <div className="fw-bold fs-5 mb-1">Notifications</div>
                        <div className="text-muted fs-2xs">
                            {getFilteredCustomers.length} • {totalSelected} selected
                        </div>
                    </div>
                </div>

                <div className="d-flex gap-1 align-items-center flex-wrap">
                    <Button className="border-0 rounded-2 fw-semibold fs-2xs transition-all d-flex align-items-center gap-1 px-3 py-2 flex-shrink-0" style={getFilterButtonStyle('all')} onClick={() => handleFilterChange('all')}>
                        <FiUsers size={14} /> All
                    </Button>
                    <Button className="border-0 rounded-2 fw-semibold fs-2xs transition-all d-flex align-items-center gap-1 px-3 py-2 flex-shrink-0" style={getFilterButtonStyle('unpaid')} onClick={() => handleFilterChange('unpaid')}>
                        <FiAlertCircle size={14} /> Unpaid
                    </Button>
                    <Button className="border-0 rounded-2 fw-semibold fs-2xs transition-all d-flex align-items-center gap-1 px-3 py-2 flex-shrink-0" style={getFilterButtonStyle('pending')} onClick={() => handleFilterChange('pending')}>
                        <FiClock size={14} /> Pending
                    </Button>
                    <Button className="border-0 rounded-2 fw-semibold fs-2xs transition-all d-flex align-items-center gap-1 px-3 py-2 flex-shrink-0" style={getFilterButtonStyle('paid')} onClick={() => handleFilterChange('paid')}>
                        <FiCheckCircle size={14} /> Paid
                    </Button>

                    <Button className="px-2 py-2 fw-semibold rounded-2 fs-2xs d-flex align-items-center gap-2 flex-shrink-0 shadow-sm" variant="primary" onClick={sendEmailNotifications} disabled={!totalSelected}>
                        <FiMail size={14} /> {totalSelected}
                    </Button>
                    <Button className="px-2 py-2 fw-semibold rounded-2 fs-2xs d-flex align-items-center gap-2 flex-shrink-0 shadow-sm" style={{ backgroundColor: '#25D366', borderColor: '#25D366', color: 'white' }} onClick={sendWhatsappNotifications} disabled={!totalSelected}>
                        <FiMessageSquare size={14} /> {totalSelected}
                    </Button>
                </div>
            </div>

            <div className="bg-white rounded-3 shadow-sm p-4">
                <div className="d-flex justify-content-between align-items-center mb-4 pb-3">
                    <div className="d-flex align-items-center gap-3 flex-grow-1 me-3">
                        <h6 className="fw-bold mb-0 fs-2xs me-3">{activeFilter === 'all' ? 'All Customers' : `${activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)} Customers`}</h6>
                        <InputGroup size="sm" className="flex-grow-1 border-0" style={{ maxWidth: '320px' }}>
                            <Form.Control
                                type="text"
                                placeholder="Search customers by name, email or phone"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="border-0 fs-2xs shadow-none py-2"
                                style={{
                                    borderRadius: '0.5rem',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #e9ecef'
                                }}
                            />
                        </InputGroup>
                    </div>

                    <div className="d-flex align-items-center gap-3">
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="p-0 fs-2xs text-primary fw-semibold text-decoration-none shadow-none"
                            onClick={handleSelectAll}
                        >
                            {isAllFilteredSelected ? 'Deselect All' : 'Select All'}
                        </Button>
                        <span className="text-muted fs-2xs fw-medium">{getFilteredCustomers.length} total</span>
                    </div>
                </div>

                <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
                    {loading ? (
                        <div className="d-flex flex-column align-items-center justify-content-center py-20 text-center text-muted">
                            <div className="spinner-border mb-4" style={{ width: '2rem', height: '2rem' }} />
                            <div className="fs-6 fw-semibold mb-1">Loading customers</div>
                            <div className="fs-2xs">Please wait while we fetch your data</div>
                        </div>
                    ) : getFilteredCustomers.length === 0 ? (
                        <div className="text-center py-16">
                            <FiUsers size={36} className="text-muted mb-2 opacity-75 mx-auto" />
                            <h5 className="text-muted mb-2 fw-semibold">No customers found</h5>
                            <p className="text-muted mb-2 fs-2xs">{searchTerm ? 'No customers match your search. Try different keywords.' : 'No customers match this filter.'}</p>
                            <Button variant="outline-primary" size="sm" className="px-2 py-1" onClick={fetchCustomers} style={{ fontSize: "12px" }}>
                                Refresh Data
                            </Button>
                        </div>
                    ) : (
                        <div className="customer-list">
                            {getFilteredCustomers.map(renderCustomerItem)}
                        </div>
                    )}
                </div>
            </div>

            <ToastContainer position="top-right" autoClose={2000} theme="colored" />
        </div>
    );
};

export default NotificationCustomers;
